import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import PropTypes from "prop-types";
import { fetchAllEmployees } from "actions";
import { CustomDropdownContainer, FormFieldLabel } from "CommonComponent";

const formatEmployeeLabel = (employee) => {
  const name =
    employee?.name ||
    [employee?.firstname, employee?.middlename, employee?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "Employee";
  const code = employee?.employee_code || employee?.employee_id || null;
  return code ? `${name} (${code})` : name;
};

const EmployeeDropdown = ({
  value = null,
  onChange,
  label = "Employee",
  placeholder = "Search employee by name or ID",
  isClearable = true,
  isDisabled = false,
  required = false,
  className = "",
}) => {
  const dispatch = useDispatch();
  const employeesList = useSelector((state) => state.employeeState.employeesList);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!employeesList?.length && !loaded) {
      setLoaded(true);
      dispatch(fetchAllEmployees());
    }
  }, [dispatch, employeesList?.length, loaded]);

  const options = useMemo(() => {
    if (!Array.isArray(employeesList)) return [];
    return employeesList
      .map((employee) => ({
        value: employee.id,
        label: formatEmployeeLabel(employee),
        employee_code: employee.employee_code || "",
        name: employee.name || "",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [employeesList]);

  return (
    <div className={className}>
      {label ? (
        <FormFieldLabel required={required}>{label}</FormFieldLabel>
      ) : null}
      <Select
        className="multi-select"
        classNamePrefix="react-select"
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable
        filterOption={(option, rawInput) => {
          const q = String(rawInput || "").trim().toLowerCase();
          if (!q) return true;
          const haystack = [
            option.label,
            option.data?.name,
            option.data?.employee_code,
            option.value,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(q);
        }}
        components={{ ValueContainer: CustomDropdownContainer }}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
      />
    </div>
  );
};

EmployeeDropdown.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
};

export default EmployeeDropdown;
